import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { websiteSettingsTabs } from '@/components/lib/SidebarLinks'
import {
  updateWebsiteAboutUs,
  updateWebsiteTerms,
  updateWebsiteContactUs,
  getAllWebsiteSettingAvailableTime,
  updateFooterSettings,
  toggleWebsiteSettingAvailableTime,
  getAllWebsiteSettings,
  getWebsiteCardSettings,
  updateWebsiteCardSettings
} from '@/api/online-ordering/settings/websiteSetting'

const initialState = {
  title: 'Logo & Banner',
  path: 'logo-banner',
  id: 1,
  aboutUs: [],
  availableTimes: [],
  availableTimeLoading: false,
  getLoading: false,
  updateLoading: false,
  cardSettings: null,
  cardSettingsLoading: false,
  success: null,
  error: null,
  requestStatus: false,
  websiteSettings: {}
}

export const updateWebsiteAbout = createAsyncThunk(
  'websiteSettingAboutUs/update',
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateWebsiteAboutUs(id, data)
    if (res.status === 'success') return res.data.setting
    return rejectWithValue(res.message)
  }
)

export const updateWebsiteContact = createAsyncThunk(
  'websiteSettingContactUs/update',
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateWebsiteContactUs(id, data)
    if (res.status === 'success') return res.data.setting
    return rejectWithValue(res.message)
  }
)

export const updateWebsiteTermsCondition = createAsyncThunk(
  'websiteSettingTermsCondition/update',
  async ({ id, terms_and_conditions }, { rejectWithValue }) => {
    const res = await updateWebsiteTerms(id, terms_and_conditions)
    if (res.status === 'success') return res.data.setting
    return rejectWithValue(res.message)
  }
)

// website setting available time
export const fetchAllWebsiteAvailableTime = createAsyncThunk(
  'websiteAvailableTime/fetchAll',
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllWebsiteSettingAvailableTime(restaurant_id)
    if (res.status === 'success') return res.data.available_times
    return rejectWithValue(res.message)
  }
)

export const updateToggleWebsiteAvailableTime = createAsyncThunk(
  'websiteAvailableTime/toggle',
  async ({ id }, { rejectWithValue }) => {
    const res = await toggleWebsiteSettingAvailableTime(id)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

// website settings
export const fetchAllWebsiteSettings = createAsyncThunk(
  'websiteSettings/fetchAll',
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllWebsiteSettings(restaurant_id)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

export const updateFooterSetting = createAsyncThunk(
  'websiteSettings/updateFooter',
  async ({ restaurant_id, data }, { rejectWithValue }) => {
    const res = await updateFooterSettings(restaurant_id, data)
    if (res.status === 'success') return res.data.setting
    return rejectWithValue(res.message)
  }
)

export const fetchCardSettings = createAsyncThunk(
  'websiteSettings/fetchCardSettings',
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getWebsiteCardSettings(restaurant_id)
    if (res && res.status === 'success') return res.data
    return rejectWithValue(res?.message || 'Failed to fetch card settings')
  }
)

export const updateCardSettings = createAsyncThunk(
  'websiteSettings/updateCardSettings',
  async ({ restaurant_id, data }, { rejectWithValue }) => {
    const res = await updateWebsiteCardSettings(restaurant_id, data)
    if (res && res.status === 'success') return res.data
    return rejectWithValue(res?.message || 'Failed to update card settings')
  }
)

const websiteSettingsSlice = createSlice({
  name: 'websiteSettings',
  initialState,
  reducers: {
    selectedTab: (state, action) => {
      const selectedItem = websiteSettingsTabs.find(
        item => item.id === action.payload
      )

      if (selectedItem) {
        state.id = selectedItem.id
        state.title = selectedItem.title
        state.path = selectedItem.path
      }
    },
    setWebsiteSettings: (state, action) => {
      state.websiteSettings = action.payload
    },
    setCardImageDisplay: (state, action) => {
      if (!state.cardSettings) {
        state.cardSettings = {}
      }
      state.cardSettings.display_card_images = action.payload
      if (state.websiteSettings?.setting) {
        state.websiteSettings.setting.display_card_images = action.payload
      }
    },
    clearWebsiteSettingMessages: state => {
      state.success = null
      state.error = null
      state.requestStatus = false
    }
  },
  extraReducers: builder => {
    builder
      // Update About Us
      .addCase(updateWebsiteAbout.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateWebsiteAbout.fulfilled, (state, action) => {
        state.updateLoading = false
        state.websiteSettings.setting = action.payload
        state.success = 'About Us updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateWebsiteAbout.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // Update Contact Us
      .addCase(updateWebsiteContact.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateWebsiteContact.fulfilled, (state, action) => {
        state.updateLoading = false

        state.websiteSettings.setting = action.payload
        state.success = 'Contact Us updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateWebsiteContact.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // Update terms and condition
      .addCase(updateWebsiteTermsCondition.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateWebsiteTermsCondition.fulfilled, (state, action) => {
        state.updateLoading = false
        state.websiteSettings.setting = action.payload
        state.success = 'Terms & condition updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateWebsiteTermsCondition.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // website setting available time
      // Fetch All
      .addCase(fetchAllWebsiteAvailableTime.pending, state => {
        state.availableTimeLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(fetchAllWebsiteAvailableTime.fulfilled, (state, action) => {
        state.availableTimeLoading = false
        state.availableTimes = action.payload
        state.requestStatus = true
      })
      .addCase(fetchAllWebsiteAvailableTime.rejected, (state, action) => {
        state.availableTimeLoading = false
        state.error = action.payload
        state.requestStatus = false
      })

      // toggle website/QRcode availability
      .addCase(updateToggleWebsiteAvailableTime.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateToggleWebsiteAvailableTime.fulfilled, (state, action) => {
        state.updateLoading = false
        state.availableTimes = state.availableTimes.map(item =>
          item.id === action.payload.id
            ? {
                ...item,
                status: action.payload.status
              }
            : item
        )

        state.success = 'Updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateToggleWebsiteAvailableTime.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })

      // Fetch All website settings
      .addCase(fetchAllWebsiteSettings.pending, state => {
        state.getLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(fetchAllWebsiteSettings.fulfilled, (state, action) => {
        state.getLoading = false
        state.websiteSettings = action.payload
        state.requestStatus = true
      })
      .addCase(fetchAllWebsiteSettings.rejected, (state, action) => {
        state.getLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      .addCase(updateFooterSetting.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateFooterSetting.fulfilled, (state, action) => {
        state.updateLoading = false
        state.websiteSettings.setting = action.payload
        state.success = 'Footer settings updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateFooterSetting.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // Fetch Card Settings
      .addCase(fetchCardSettings.pending, state => {
        state.cardSettingsLoading = true
        state.error = null
      })
      .addCase(fetchCardSettings.fulfilled, (state, action) => {
        state.cardSettingsLoading = false
        state.cardSettings = action.payload
        if (state.websiteSettings?.setting && action.payload) {
          state.websiteSettings.setting.display_card_images =
            action.payload.display_card_images
        }
      })
      .addCase(fetchCardSettings.rejected, (state, action) => {
        state.cardSettingsLoading = false
        state.error = action.payload
      })
      // Update Card Settings
      .addCase(updateCardSettings.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateCardSettings.fulfilled, (state, action) => {
        state.updateLoading = false
        state.cardSettings = action.payload
        if (state.websiteSettings?.setting && action.payload) {
          state.websiteSettings.setting.display_card_images =
            action.payload.display_card_images
        }
        state.success = 'Card settings updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateCardSettings.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
  }
})

export const { selectedTab, clearWebsiteSettingMessages, setWebsiteSettings, setCardImageDisplay } =
  websiteSettingsSlice.actions
export default websiteSettingsSlice.reducer
